import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { initAnalytics, disableAnalytics } from '../../utils/analytics';
import './CookieConsentBanner.css';

const STORAGE_KEY = 'cookie_consent';

const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [analyticsChecked, setAnalyticsChecked] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      setVisible(true);
    }
    // "Cookie settings" in any footer reopens the banner so consent can be
    // changed or withdrawn at any time (ePrivacy withdrawal right).
    const reopen = () => {
      try {
        const current = JSON.parse(localStorage.getItem(STORAGE_KEY));
        setAnalyticsChecked(Boolean(current?.analytics));
      } catch { /* ignore */ }
      setShowCustomize(true);
      setVisible(true);
    };
    window.addEventListener('awfm:cookie-settings', reopen);
    return () => window.removeEventListener('awfm:cookie-settings', reopen);
  }, []);

  const saveConsent = (consent) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...consent, at: new Date().toISOString() }));
    if (consent.analytics) {
      initAnalytics();
    } else {
      disableAnalytics();
    }
    // Signed-in users: mirror the choice into the consent ledger so it's
    // demonstrable (Art. 7). Anonymous visitors keep it in the browser only.
    try {
      if (localStorage.getItem('authTokens')) {
        import('../../services/api').then(({ default: api }) =>
          api
            .post(consent.analytics ? '/v1/auth/consent/record/' : '/v1/auth/consent/withdraw/', {
              consent_type: 'cookie_analytics',
            })
            .catch(() => {}),
        );
      }
    } catch { /* ignore */ }
    setVisible(false);
  };

  const handleEssentialOnly = () => {
    saveConsent({ essential: true, analytics: false });
  };

  const handleAcceptAll = () => {
    saveConsent({ essential: true, analytics: true });
  };

  const handleSavePreferences = () => {
    saveConsent({ essential: true, analytics: analyticsChecked });
  };

  if (!visible) return null;

  return (
    <div className="cookie-banner" role="dialog" aria-label="Cookie consent">
      <div className="cookie-banner__content">
        <p className="cookie-banner__text">
          We use cookies to keep the platform working (essential) and to understand
          how people use it (analytics). You can accept just the essential cookies
          or all cookies.{' '}
          <Link to="/cookie-policy">Cookie Policy</Link>
        </p>
        <div className="cookie-banner__actions">
          <button
            className="cookie-banner__btn cookie-banner__btn--secondary"
            onClick={handleEssentialOnly}
          >
            Essential Only
          </button>
          <button
            className="cookie-banner__btn cookie-banner__btn--primary"
            onClick={handleAcceptAll}
          >
            Accept All
          </button>
          <button
            className="cookie-banner__btn cookie-banner__btn--text"
            onClick={() => setShowCustomize((prev) => !prev)}
            aria-expanded={showCustomize}
          >
            Customize
          </button>
        </div>
      </div>

      {showCustomize && (
        <div className="cookie-banner__customize">
          <div className="cookie-banner__customize-options">
            <label className="cookie-banner__checkbox-label cookie-banner__checkbox-label--disabled">
              <input
                type="checkbox"
                checked
                disabled
                readOnly
              />
              Essential Cookies
              <span className="cookie-banner__required-tag">(required)</span>
            </label>

            <label className="cookie-banner__checkbox-label">
              <input
                type="checkbox"
                checked={analyticsChecked}
                onChange={(e) => setAnalyticsChecked(e.target.checked)}
              />
              Analytics Cookies
            </label>

            <button
              className="cookie-banner__btn cookie-banner__btn--primary"
              onClick={handleSavePreferences}
            >
              Save Preferences
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CookieConsentBanner;
